import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './AddClinicalSitesModal.module.scss';
import Modal from '../../../components/modal/Modal';
import Button from '../../../components/button/Button';
import LiveSearch from '../../../components/liveSearch/LiveSearch';
import axiosClient from '../../../api/axiosClient';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import { useToasts } from '../../../contexts/ToastContext';
import ProgramClinicalSitesTable from './ProgramClinicalSitesTable';
import Icon from '../../../components/icon/Icon';

const AddClinicalSitesModal = ({ isOpen, onClose, programId, onSuccess }) => {
    const { showToast } = useToasts();
    const [allSites, setAllSites] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSiteIds, setSelectedSiteIds] = useState([]);

    useEffect(() => {
        if (isOpen) {
            setSelectedSiteIds([]);
            setSearchQuery('');
        }
    }, [isOpen]);

    useEffect(() => {
        const fetchSitesData = async () => {
            if (!isOpen) return;
            
            setIsLoading(true);
            try {
                const [allSitesRes, assignedSitesRes] = await Promise.all([
                    axiosClient.get(`/programs/clinical-sites/list`),
                    programId ? axiosClient.get(`/programs/${programId}/clinical-sites`) : Promise.resolve({ data: [] })
                ]);

                const fetchedSites = allSitesRes.data?.data || allSitesRes.data?.clinicalSites || allSitesRes.data;
                setAllSites(Array.isArray(fetchedSites) ? fetchedSites : []);

                if (programId && assignedSitesRes?.data) {
                    const assignedData = assignedSitesRes.data?.data || assignedSitesRes.data?.clinicalSites || assignedSitesRes.data;
                    const assignedSites = Array.isArray(assignedData) ? assignedData : [];
                    setSelectedSiteIds(assignedSites.map(site => site.id));
                }
            } catch (error) {
                console.error("Failed to fetch clinical sites data", error);
                showToast(error.message || 'Failed to fetch clinical sites', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSitesData();
    }, [isOpen, programId, showToast]);

    const handleSave = async () => {
        if (!programId) return;
        
        setIsSaving(true);
        try {
            await axiosClient.post(`/programs/${programId}/assign-clinical-sites`, {
                clinicalSiteIds: selectedSiteIds
            });
            showToast('Clinical sites assigned successfully', 'success');
            if (onSuccess) {
                onSuccess();
            }
            onClose();
        } catch (error) {
            showToast(error.message || 'Failed to assign clinical sites', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const getFilteredSites = () => {
        if (!searchQuery) return allSites;
        const query = searchQuery.toLowerCase();
        return allSites.filter(s => s.name?.toLowerCase().includes(query));
    };

    if (!isOpen) return null;

    return (
        <Modal scrollable={true}>
            {(isLoading || isSaving) && <LoadingOverlay />}
            <div className={styles.modalContent}>
                <div className={styles.header}>
                    <h2 className={styles.title}>Add Clinical Site</h2>
                    <div className={styles.actions}>
                        <LiveSearch 
                            placeholder="Search clinical sites" 
                            onChange={(val) => setSearchQuery(val)} 
                        />
                        <Button 
                            label="Cancel" 
                            variant="primary-light"
                            onClick={onClose}
                            size="medium"
                        />
                        <Button 
                            label={
                                <div className={styles.cta}>
                                    <Icon name="save" />
                                    Save Changes
                                </div>
                            }
                            variant="primary"
                            onClick={handleSave}
                            size="medium"
                            disabled={selectedSiteIds.length === 0}
                        />
                    </div>
                </div>

                <div className={styles.tableWrapper}>
                    <ProgramClinicalSitesTable 
                        clinicalSites={getFilteredSites()}
                        isSelectable={true}
                        onRowSelect={(selectedIds) => setSelectedSiteIds(selectedIds)}
                        selectedRows={selectedSiteIds}
                    />
                </div>
            </div>
        </Modal>
    );
};

AddClinicalSitesModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuccess: PropTypes.func
};

export default AddClinicalSitesModal;
